import React, { useEffect, useMemo, useState } from "react";
import Sidebarmenu from "../../Sidebar/Sidebarmenu";
import Header from "../Header/Header";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { CSVLink } from "react-csv";
import ReactApexChart from "react-apexcharts";
import { Inventory2, Kitchen, AcUnit } from "@mui/icons-material";
import calender from "../../../img/Icon/calender.svg";
import loadergif from "../../../img/landing/loader123.gif";
import {
  getInventoryOrderedByIngredient,
  getInventoryOrderedByDay,
} from "../../../Redux/Reducer/AuthReducer";

// Any of these read as "no value" from this API (alongside actual null) -
// package/vendor fields especially come back as the literal string "NULL"
// rather than a JSON null.
function isBlank(value) {
  return value === null || value === undefined || value === "" || value === "NULL" || value === "null";
}

function formatQty(value) {
  if (isBlank(value)) return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// package_size/package_type both describe the same package, shown together
// (e.g. "25 lb box" + "Bag" -> "25 lb box - Bag"); package_unit (the unit
// quantities are measured in, e.g. "Lbs") is kept as its own column instead.
function formatPackage(item) {
  const parts = [item.package_size, item.package_type].filter((p) => !isBlank(p));
  return parts.length > 0 ? parts.join(" - ") : "-";
}

// Up to 3 vendor name columns on this API, collapsed into one comma-joined
// column - most rows only ever have 1-2 actually filled in. Only used by the
// By Ingredient view - By Week drops vendor_name2/3 as columns entirely, so
// it just reads vendor_name1 directly.
function formatVendors(item) {
  const vendors = [item.vendor_name1, item.vendor_name2, item.vendor_name3].filter((v) => !isBlank(v));
  return vendors.length > 0 ? vendors.join(", ") : "-";
}

const STORAGE_CHART_COLORS = { Storage: "#957137", Fridge: "#1565C0", Freezer: "#5C5EC1" };
const STORAGE_ICONS = { Storage: Inventory2, Fridge: Kitchen, Freezer: AcUnit };
const STORAGE_ORDER = Object.keys(STORAGE_CHART_COLORS);

// These are the only 3 real values this API's storage_location ever sends -
// there's no "Other"/unset case in practice, so unlike an ordinary bucketing
// helper this returns null (excluded everywhere below) rather than lumping
// anything unexpected into a catch-all bucket. Shared by the pie chart, the
// By Ingredient storage sections, and the table's pie-slice filter, so all
// three always agree on what counts as what.
function getStorageBucket(item) {
  return STORAGE_ORDER.includes(item.storage_location) ? item.storage_location : null;
}

export default function InventoryReport() {
  const dispatch = useDispatch();
  const [viewMode, setViewMode] = useState("ingredient"); // "ingredient" | "week"
  const [startDate, setStartDate] = useState(moment().subtract(59, "days").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [ingredientData, setIngredientData] = useState([]);
  const [weekData, setWeekData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  // Set by clicking a slice of the storage pie chart - null means no filter.
  const [storageFilter, setStorageFilter] = useState(null);

  useEffect(() => {
    setLoading(true);
    const thunk = viewMode === "week" ? getInventoryOrderedByDay : getInventoryOrderedByIngredient;
    dispatch(thunk({ start_date: startDate, end_date: endDate }))
      .then((res) => {
        if (res?.payload?.status === true) {
          const rows = Array.isArray(res.payload.data) ? res.payload.data : [];
          if (viewMode === "week") {
            setWeekData(rows);
          } else {
            setIngredientData(rows);
          }
        } else {
          toast.error(res?.payload?.message || "Failed to load inventory report data");
        }
      })
      .catch(() => toast.error("Failed to load inventory report data"))
      .finally(() => setLoading(false));
  }, [dispatch, viewMode, startDate, endDate]);

  // react-bootstrap-daterangepicker hands back "MM/DD/YYYY - MM/DD/YYYY" in
  // event.target.value (default locale) - same parsing this app's other
  // DateRangePicker usages already do (see ProcurementCombined.js).
  const handleDateRangeApply = (event) => {
    const [rawStart, rawEnd] = event.target.value.split(" - ");
    const parsed = moment(rawStart, "MM/DD/YYYY");
    const parsedEnd = moment(rawEnd, "MM/DD/YYYY");
    if (!parsed.isValid() || !parsedEnd.isValid()) return;
    setStartDate(parsed.format("YYYY-MM-DD"));
    setEndDate(parsedEnd.format("YYYY-MM-DD"));
    // A new range can easily no longer match the old filter/search - drop
    // both rather than silently show an empty table.
    setStorageFilter(null);
    setSearchQuery("");
  };

  // Ingredients with nothing to order are dropped for this whole view - the
  // table, the storage chart, the total count, and the CSV export all read
  // from this instead of the raw ingredientData.
  const orderedIngredientData = useMemo(
    () => ingredientData.filter((item) => Number(item.order_quantity) !== 0),
    [ingredientData]
  );

  const sortedIngredientData = useMemo(
    () =>
      [...orderedIngredientData].sort((a, b) => {
        if (isBlank(a.ingredient_name)) return 1;
        if (isBlank(b.ingredient_name)) return -1;
        return a.ingredient_name.localeCompare(b.ingredient_name);
      }),
    [orderedIngredientData]
  );

  // Search + the storage pie-chart filter both narrow what's shown on
  // screen (and what the Total Ingredients count below reflects) - CSV
  // export deliberately ignores both and always exports the full data for
  // the selected date range, since "export report" reads as exporting the
  // report, not just whatever the user happened to have filtered to.
  const displayedIngredientData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return sortedIngredientData.filter((item) => {
      if (q && (isBlank(item.ingredient_name) || !item.ingredient_name.toLowerCase().includes(q))) {
        return false;
      }
      if (storageFilter && getStorageBucket(item) !== storageFilter) return false;
      return true;
    });
  }, [sortedIngredientData, searchQuery, storageFilter]);

  // By Ingredient, sectioned by storage location - same section-header
  // pattern as the By Week view's week grouping. displayedIngredientData is
  // already alphabetically sorted, so each section's rows stay alphabetical
  // too without re-sorting. Only non-empty sections render (e.g. clicking a
  // pie slice leaves just that one storage's section).
  const ingredientGroups = useMemo(() => {
    const buckets = { Storage: [], Fridge: [], Freezer: [] };
    displayedIngredientData.forEach((item) => {
      const bucket = getStorageBucket(item);
      if (bucket) buckets[bucket].push(item);
    });
    return STORAGE_ORDER.filter((storage) => buckets[storage].length > 0).map((storage) => ({
      storage,
      rows: buckets[storage],
    }));
  }, [displayedIngredientData]);

  // Count of ingredients per storage_location - a quick "what's most of our
  // pantry stored where" overview, not a quantity total (menu_quantity/
  // order_quantity are in mixed units - Lbs, Gallon, Pieces, ... - across
  // ingredients, so summing them together wouldn't mean anything). Built
  // from orderedIngredientData (order_quantity 0 already dropped) but not
  // displayedIngredientData, so the search box/slice filter narrowing the
  // table doesn't also reshape the chart summarizing it.
  const storageChartData = useMemo(() => {
    const counts = { Storage: 0, Fridge: 0, Freezer: 0 };
    orderedIngredientData.forEach((item) => {
      if (isBlank(item.ingredient_name)) return;
      const bucket = getStorageBucket(item);
      if (bucket) counts[bucket] += 1;
    });
    const labels = STORAGE_ORDER.filter((label) => counts[label] > 0);
    return {
      labels,
      series: labels.map((label) => counts[label]),
      colors: labels.map((label) => STORAGE_CHART_COLORS[label]),
    };
  }, [orderedIngredientData]);

  // Clicking a slice (or its matching legend button) filters the table to
  // that storage location; clicking the already-active one again clears it.
  const toggleStorageFilter = (label) => {
    setStorageFilter((prev) => (prev === label ? null : label));
  };
  const handleStorageSliceClick = (event, chartContext, config) => {
    const label = storageChartData.labels[config.dataPointIndex];
    if (label) toggleStorageFilter(label);
  };

  // Ingredients with nothing left to order this week are dropped entirely.
  const orderedWeekRows = useMemo(
    () => weekData.filter((item) => Number(item.order_quantity) !== 0),
    [weekData]
  );

  // NOT confirmed against a real sample of this endpoint's response -
  // assumes it returns the same per-row shape as By Ingredient (ti_id, id,
  // ingredient_name, ..., start_date, end_date) but one row per ingredient
  // PER WEEK rather than aggregated across the whole range. Grouped by the
  // (start_date, end_date) pair rather than ti_id - ti_id turned out to be
  // unique per row even for ingredients in the same week (confirmed against
  // real data), so it can't be the group key; the week's own date range is
  // the only thing rows in the same week are guaranteed to share.
  const weekGroups = useMemo(() => {
    const groups = new Map();
    orderedWeekRows.forEach((item) => {
      const key = `${item.start_date}_${item.end_date}`;
      if (!groups.has(key)) {
        groups.set(key, { weekStart: item.start_date, weekEnd: item.end_date, rows: [] });
      }
      groups.get(key).rows.push(item);
    });
    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        rows: [...group.rows].sort((a, b) => {
          if (isBlank(a.ingredient_name)) return 1;
          if (isBlank(b.ingredient_name)) return -1;
          return a.ingredient_name.localeCompare(b.ingredient_name);
        }),
      }))
      .sort((a, b) => new Date(a.weekStart) - new Date(b.weekStart));
  }, [orderedWeekRows]);

  // Raw values (not the "-"-for-blank/formatted-number display strings the
  // table uses) so the CSV stays machine-readable - numbers import as
  // numbers, blanks import as empty cells, in whatever spreadsheet app opens
  // it.
  const csvHeaders = useMemo(() => {
    if (viewMode === "week") {
      return [
        { label: "Week Start", key: "start_date" },
        { label: "Week End", key: "end_date" },
        { label: "Ingredient", key: "ingredient_name" },
        { label: "Unit", key: "package_unit" },
        { label: "Package Size", key: "package_size" },
        { label: "Package Type", key: "package_type" },
        { label: "Vendor", key: "vendor_name1" },
        { label: "Storage", key: "storage_location" },
        { label: "Menu Qty", key: "menu_quantity" },
        { label: "Order Qty", key: "order_quantity" },
      ];
    }
    return [
      { label: "Ingredient", key: "ingredient_name" },
      { label: "Unit", key: "package_unit" },
      { label: "Package Size", key: "package_size" },
      { label: "Package Type", key: "package_type" },
      { label: "Vendor 1", key: "vendor_name1" },
      { label: "Vendor 2", key: "vendor_name2" },
      { label: "Vendor 3", key: "vendor_name3" },
      { label: "Storage", key: "storage_location" },
      { label: "Menu Qty", key: "menu_quantity" },
      { label: "Order Qty", key: "order_quantity" },
    ];
  }, [viewMode]);

  const csvData = useMemo(() => {
    const rows = viewMode === "week" ? orderedWeekRows : sortedIngredientData;
    return rows.map((row) => {
      const cleaned = {};
      Object.keys(row).forEach((key) => {
        cleaned[key] = isBlank(row[key]) ? "" : row[key];
      });
      return cleaned;
    });
  }, [viewMode, orderedWeekRows, sortedIngredientData]);

  const csvFileName = `Inventory-Report-${viewMode === "week" ? "By-Week" : "By-Ingredient"}-${startDate}-to-${endDate}.csv`;

  return (
    <div>
      <Header />
      <div className="side_div_menu" style={{ display: "flex", height: "100%" }}>
        <Sidebarmenu />
        <section className="main_section">
          <div className="custam">
            <div className="main_section2">
              <div className="ExportInventory thali_section Thali_page">
                <div className="container-fluid">
                  <div className="thili_div inventory-report-header-row">
                    <div className="Back-page">
                      <Link to="/ViewInventory">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M7.175 15.4748L0.225 8.5248C0.141667 8.44147 0.0833335 8.35814 0.0500002 8.2748C0.0166669 8.19147 0 8.0998 0 7.9998C0 7.8998 0.0166669 7.80814 0.0500002 7.7248C0.0833335 7.64147 0.141667 7.55814 0.225 7.4748L7.2 0.499804C7.33333 0.366471 7.5 0.299805 7.7 0.299805C7.9 0.299805 8.075 0.374805 8.225 0.524805C8.375 0.674805 8.45 0.849805 8.45 1.0498C8.45 1.2498 8.375 1.4248 8.225 1.5748L2.55 7.2498H14.95C15.1667 7.2498 15.3458 7.32064 15.4875 7.4623C15.6292 7.60397 15.7 7.78314 15.7 7.9998C15.7 8.21647 15.6292 8.39564 15.4875 8.5373C15.3458 8.67897 15.1667 8.7498 14.95 8.7498H2.55L8.25 14.4498C8.38333 14.5831 8.45 14.7498 8.45 14.9498C8.45 15.1498 8.375 15.3248 8.225 15.4748C8.075 15.6248 7.9 15.6998 7.7 15.6998C7.5 15.6998 7.325 15.6248 7.175 15.4748Z"
                            fill="#777391"
                          />
                        </svg>
                      </Link>
                      <h1 className="title_heading">&nbsp; &nbsp; &nbsp; Inventory Report</h1>
                    </div>

                    <div className="New_section">
                      <div className="Form_box">
                        <div className="Calender_div">
                          <img src={calender} className="img-fluid" alt="" /> &nbsp; &nbsp;
                          <DateRangePicker
                            initialSettings={{
                              startDate: moment(startDate).format("MM/DD/YYYY"),
                              endDate: moment(endDate).format("MM/DD/YYYY"),
                            }}
                            onApply={handleDateRangeApply}
                          >
                            <input type="text" className="form-control" />
                          </DateRangePicker>
                        </div>
                      </div>
                      <span className="Line_div">|</span>
                      <button className="btn thali_btn csvbtn">
                        <CSVLink data={csvData} headers={csvHeaders} filename={csvFileName}>
                          Export Report
                        </CSVLink>
                      </button>
                    </div>
                  </div>

                  <div className="inventory-toggle-chart-row">
                    <div className="mp-view-toggle">
                      <div className="mp-view-toggle-group">
                        <button
                          type="button"
                          className={`btn thali_btn mp-toggle-btn ${viewMode === "ingredient" ? "active" : ""}`}
                          onClick={() => setViewMode("ingredient")}
                        >
                          By Ingredient
                        </button>
                        <button
                          type="button"
                          className={`btn thali_btn mp-toggle-btn ${viewMode === "week" ? "active" : ""}`}
                          onClick={() => setViewMode("week")}
                        >
                          By Week
                        </button>
                      </div>
                    </div>

                    {viewMode === "ingredient" && storageChartData.labels.length > 0 && (
                      <div className="inventory-storage-chart-card">
                        <div className="inventory-storage-chart-header">
                          <span>Ingredients by Storage</span>
                          {storageFilter && (
                            <button
                              type="button"
                              className="inventory-storage-chart-clear"
                              onClick={() => setStorageFilter(null)}
                            >
                              Clear ({storageFilter}) &times;
                            </button>
                          )}
                        </div>
                        <div className="inventory-storage-chart-body">
                          <ReactApexChart
                            type="pie"
                            width={90}
                            height={90}
                            series={storageChartData.series}
                            options={{
                              labels: storageChartData.labels,
                              colors: storageChartData.colors,
                              legend: { show: false },
                              dataLabels: { enabled: false },
                              stroke: { width: 1 },
                              tooltip: { y: { formatter: (val) => `${val} ingredients` } },
                              chart: { events: { dataPointSelection: handleStorageSliceClick } },
                            }}
                          />
                          <ul className="inventory-storage-chart-legend">
                            {storageChartData.labels.map((label, i) => {
                              const StorageIcon = STORAGE_ICONS[label];
                              return (
                                <li key={label}>
                                  <button
                                    type="button"
                                    className={`inventory-storage-chart-legend-item ${
                                      storageFilter === label ? "is-active" : ""
                                    }`}
                                    onClick={() => toggleStorageFilter(label)}
                                  >
                                    <StorageIcon fontSize="inherit" style={{ color: storageChartData.colors[i] }} />
                                    {label} ({storageChartData.series[i]})
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>

                  {viewMode === "ingredient" && (
                    <div className="inventory-report-toolbar-row">
                      <div className="inventory-search-box">
                        <span className="fa fa-search"></span>
                        <input
                          type="text"
                          className="inventory-search-input"
                          placeholder="Search ingredient by name"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                          <button
                            type="button"
                            className="inventory-search-clear"
                            onClick={() => setSearchQuery("")}
                            aria-label="Clear search"
                          >
                            &times;
                          </button>
                        )}
                      </div>
                      <div className="mp-grid-summary-tile inventory-total-count-tile">
                        <span className="mp-grid-summary-value">{displayedIngredientData.length}</span>
                        <span className="mp-grid-summary-label">Total Ingredients</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="thali_table_section">
                  {loading ? (
                    <div className="Loading-img">
                      <img src={loadergif} alt="Loading" />
                    </div>
                  ) : viewMode === "ingredient" ? (
                    ingredientGroups.length > 0 ? (
                      <table className="table table-bordered table-responsive">
                        <thead className="thead-dark">
                          <tr>
                            <th scope="col"><span className="thali_table">Ingredient</span></th>
                            <th scope="col"><span className="thali_table">Unit</span></th>
                            <th scope="col"><span className="thali_table">Package</span></th>
                            <th scope="col"><span className="thali_table">Vendor(s)</span></th>
                            <th scope="col"><span className="thali_table">Menu Qty</span></th>
                            <th scope="col"><span className="thali_table">Order Qty</span></th>
                          </tr>
                        </thead>
                        {ingredientGroups.map((group) => {
                          const StorageIcon = STORAGE_ICONS[group.storage];
                          return (
                          <tbody key={group.storage}>
                            <tr
                              className="inventory-storage-section-row"
                              style={{ "--inventory-storage-color": STORAGE_CHART_COLORS[group.storage] }}
                            >
                              <td colSpan={6}>
                                <div className="inventory-week-section-row-inner">
                                  <span className="inventory-week-section-label">
                                    <StorageIcon fontSize="inherit" className="inventory-storage-section-icon" />
                                    {group.storage}
                                  </span>
                                  <span className="inventory-week-section-count">
                                    {group.rows.length} ingredient{group.rows.length === 1 ? "" : "s"}
                                  </span>
                                </div>
                              </td>
                            </tr>
                            {group.rows.map((item) => (
                              <tr key={item.ti_id}>
                                <td>{isBlank(item.ingredient_name) ? "-" : item.ingredient_name}</td>
                                <td>{isBlank(item.package_unit) ? "-" : item.package_unit}</td>
                                <td>{formatPackage(item)}</td>
                                <td>{formatVendors(item)}</td>
                                <td>{formatQty(item.menu_quantity)}</td>
                                <td>{formatQty(item.order_quantity)}</td>
                              </tr>
                            ))}
                          </tbody>
                          );
                        })}
                      </table>
                    ) : (
                      <p className="mp-empty-note">
                        {searchQuery.trim() || storageFilter
                          ? "No ingredient matches the current search/filter for this date range."
                          : "No ingredient data found for this date range."}
                      </p>
                    )
                  ) : weekGroups.length > 0 ? (
                    <table className="table table-bordered table-responsive">
                      <thead className="thead-dark">
                        <tr>
                          <th scope="col"><span className="thali_table">Ingredient</span></th>
                          <th scope="col"><span className="thali_table">Unit</span></th>
                          <th scope="col"><span className="thali_table">Package</span></th>
                          <th scope="col"><span className="thali_table">Vendor</span></th>
                          <th scope="col"><span className="thali_table">Storage</span></th>
                          <th scope="col"><span className="thali_table">Menu Qty</span></th>
                          <th scope="col"><span className="thali_table">Order Qty</span></th>
                        </tr>
                      </thead>
                      {weekGroups.map((group) => (
                        <tbody key={`${group.weekStart}_${group.weekEnd}`}>
                          <tr className="inventory-week-section-row">
                            <td colSpan={7}>
                              <div className="inventory-week-section-row-inner">
                                <span className="inventory-week-section-label">
                                  <img src={calender} className="img-fluid" alt="" />
                                  Week of {moment(group.weekStart).format("MMM D")} &ndash;{" "}
                                  {moment(group.weekEnd).format("MMM D, YYYY")}
                                </span>
                                <span className="inventory-week-section-count">
                                  {group.rows.length} ingredient{group.rows.length === 1 ? "" : "s"}
                                </span>
                              </div>
                            </td>
                          </tr>
                          {group.rows.map((item) => (
                            <tr key={item.ti_id}>
                              <td>{isBlank(item.ingredient_name) ? "-" : item.ingredient_name}</td>
                              <td>{isBlank(item.package_unit) ? "-" : item.package_unit}</td>
                              <td>{formatPackage(item)}</td>
                              <td>{isBlank(item.vendor_name1) ? "-" : item.vendor_name1}</td>
                              <td>{isBlank(item.storage_location) ? "-" : item.storage_location}</td>
                              <td>{formatQty(item.menu_quantity)}</td>
                              <td>{formatQty(item.order_quantity)}</td>
                            </tr>
                          ))}
                        </tbody>
                      ))}
                    </table>
                  ) : (
                    <p className="mp-empty-note">No week data found for this date range.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
